import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import { FeatureFlags } from '@constants/featureFlags';
import { paths } from '@constants/paths';
import { submitBroadbandRequirementsQuery } from '@features/Broadband/query';
import {
  BundleBasketFragment,
  Occupancy,
  PartnerConnector,
} from '@generated/gql/graphql';
import { ProductType } from '@generated/gql/graphql';
import { useOrderSubmitter } from '@hooks/submitOrder/useOrderSubmitter';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { useSnackbarErrors } from '@hooks/useSnackbarErrors';
import { BooleanSelection } from '@src/types/index';
import { YourDetailsFormValues } from '@src/types/yourDetails';
import { parseReferrerContext } from '@tracking/utils/parseReferrerContext';
import { thankYouDetailsStorage } from '@utils/storagePropertyManagement';

import { BASKET_DATA_MISMATCH_ERROR } from '../../constants';
import { parseCheckoutTrackingContext } from '../checkoutTrackingContext';
import { parseAttributionInput } from '../parseAttributionInput';
import { useSubmitApplication } from '../useSubmitApplication';
import { useUpdateCbcRequirements } from '../useUpdateCbcRequirements';
import { formatFormValues } from './format';
import { getBankToken } from './getBankToken';
import { handleSubmitErrors } from './handleSubmitErrors';
import { useTrackReferralOnSubmit } from './useTrackReferralOnSubmit';

interface UseSubmitProps {
  basket: Nullable<BundleBasketFragment>;
  productTypes: ProductType[];
  isDigitalHomePhoneSelected: boolean;
}

const formatResponseError = (error: unknown) =>
  Array.isArray(error) ? error : [error];

const useSubmit = ({
  basket,
  productTypes,
  isDigitalHomePhoneSelected,
}: UseSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [submitErrors, setSubmitErrors] = useState<ErrorLike[]>([]);

  const { updateCbcAccountHolders } = useUpdateCbcRequirements(productTypes);
  const submitApplication = useSubmitApplication();
  const { submitAndCompleteOrder, submitOrderLoading } = useOrderSubmitter();
  const paperBilling = useDialog(DialogID.PAPER_BILLING);
  const { value: isPaperBillingEnabled } = useFeatureFlag(
    FeatureFlags.PAPER_BILLING_ENABLED,
    false
  );

  const [submitBroadbandRequirements] = useMutation(
    submitBroadbandRequirementsQuery
  );

  useSnackbarErrors(submitErrors);

  const trackReferralOnSubmit = useTrackReferralOnSubmit();

  const handleSubmit = async (
    values: YourDetailsFormValues,
    occupancyType: Occupancy,
    connectors: Nullable<PartnerConnector[]>,
    setFieldError: (field: string, message: string) => void
  ) => {
    if (!basket) {
      throw new Error(BASKET_DATA_MISMATCH_ERROR);
    }
    setSubmitErrors([]);
    setIsSubmitting(true);
    trackReferralOnSubmit(values);

    try {
      await updateCbcAccountHolders(basket, values);
    } catch (error) {
      handleSubmitErrors({
        setSubmitErrors,
        setFieldError,
        submitResponseErrors: formatResponseError(error),
      });
      setIsSubmitting(false);
      return;
    }

    const applicationInput = formatFormValues(values, occupancyType);
    const referrerInput = parseAttributionInput(values, connectors);
    const referrerContext = parseReferrerContext(values, connectors);

    const hasEmail =
      Boolean(applicationInput.accountHolders[0].accountDetails.email) &&
      applicationInput.accountHolders[0].accountDetails.email?.length;

    if (isPaperBillingEnabled) {
      if (!hasEmail && !paperBilling.open) {
        paperBilling.setOpen(true);
        setIsSubmitting(false);
        return;
      }

      // should match subsequent submission attempt from the
      // paper billing modal, confirming a lack of email address
      if (!hasEmail && paperBilling.open) {
        paperBilling.setOpen(false);
      }
    }

    const bankToken = await getBankToken({
      setFieldError,
      setSubmitErrors,
      bankDetails: applicationInput.directDebit,
    });

    if (!bankToken) {
      setIsSubmitting(false);
      return;
    }

    try {
      const submitResponse = await submitApplication(
        bankToken,
        applicationInput,
        referrerInput
      );
      if (isDigitalHomePhoneSelected) {
        const isVulnerable: boolean =
          values.broadbandEmergMonitDevice === BooleanSelection.Yes ||
          values.broadbandEmergAlt === BooleanSelection.Yes;
        await submitBroadbandRequirements({
          variables: {
            details: { basketId: basket.id, isVulnerable },
          },
        });
      }

      const evInterest =
        basket.energy?.details?.electricVehicleTariffExpressionOfInterest ||
        false;

      const canSwitch = productTypes.includes(ProductType.Broadband);

      const checkoutTrackingContext = parseCheckoutTrackingContext(
        basket,
        applicationInput,
        values,
        canSwitch,
        evInterest,
        referrerContext
      );

      thankYouDetailsStorage.set({
        primaryFirstName:
          applicationInput.accountHolders[0].accountDetails.firstName ?? '',
        hasJointAccountHolder: applicationInput.accountHolders.length > 1,
        recentlyMovedIn: applicationInput.recentlyMovedIn ?? false,
      });

      const applicationResult = submitResponse.data?.setApplication;
      if (applicationResult?.depositRequirement?.depositRequired) {
        navigate(paths.deposit);
      } else {
        await submitAndCompleteOrder({ checkoutTrackingContext });
      }
    } catch (error) {
      handleSubmitErrors({
        setSubmitErrors,
        setFieldError,
        submitResponseErrors: formatResponseError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isLoading: submitOrderLoading || isSubmitting,
    handleSubmit,
  };
};

export { useSubmit };
